import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, CheckCircle, XCircle, AlertTriangle, Info, History } from 'lucide-react';
import clsx from 'clsx';
import { showToast } from '@utils/toast';
import { formatMediumDateTime } from '@utils/dateUtils';
import { getScopeLabel } from '@utils/referenceCatalog';
import { importService, contractService, divisionService, supplierTierAdminService } from '@services';
import Button from '@components/common/Button';
import SettingsSectionHeader from '@components/features/data-management-settings/SettingsSectionHeader';
import { useReportDirty } from '@components/features/data-management-settings/useReportDirty';
import type { SettingsPaneProps } from '@components/features/data-management-settings/settingsNav';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import { Pagination } from '@components/common/table';
import { SCOPE_RULES_HISTORY_PAGE_SIZE } from '@app-types/import';
import type {
  ScopeRuleUpdatePayload,
  ScopeRecalculationRunResponse,
  ScopeRulesHistoryResponse,
} from '@app-types/import';
import type { AuditHistoryEntry } from '@app-types';
import {
  flatRulesToGrouped,
  mergeListRules,
  mergeTierRules,
  listRulesToPayload,
} from './scopeRulesHelpers';
import type { ListRule, ScopeRules, ThresholdRule } from './scopeRulesHelpers';
import styles from './ScopeRulesSettings.module.css';

const EMPTY_LOOKUP_HELP = 'Values appear after they are used on contracts or imports.';

const emptyHistory = (): ScopeRulesHistoryResponse => ({
  records: [],
  total: 0,
  limit: SCOPE_RULES_HISTORY_PAGE_SIZE,
  offset: 0,
});

const ScopeRulesSettings: React.FC<SettingsPaneProps> = ({ onDirtyChange }) => {
  const [rules, setRules] = useState<ScopeRules | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [globalScopeCalculation, setGlobalScopeCalculation] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [latestRun, setLatestRun] = useState<ScopeRecalculationRunResponse | null>(null);
  const [history, setHistory] = useState<AuditHistoryEntry[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  useReportDirty(hasChanges, onDirtyChange);

  const [tierRules, setTierRules] = useState<ListRule[]>([]);
  const [contractTypeRules, setContractTypeRules] = useState<ListRule[]>([]);
  const [categoryRules, setCategoryRules] = useState<ListRule[]>([]);
  const [divisionRules, setDivisionRules] = useState<ListRule[]>([]);
  const [amountThreshold, setAmountThreshold] = useState<number>(0);
  const [durationThreshold, setDurationThreshold] = useState<number>(60);

  useEffect(() => {
    void fetchRules();
  }, []);

  useEffect(() => {
    if (latestRun?.status !== 'running') {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void importService.getLatestRecalculation()
        .then((run) => {
          setLatestRun(run);
          if (run?.status === 'completed') {
            showToast.success(
              `Recalculated ${run.total} contracts (${run.include_count} included, ${run.exclude_count} excluded${run.flagged ? `, ${run.flagged} flagged` : ''})`
            );
            if (run.error_count > 0) {
              showToast.error(`${run.error_count} contracts failed to recalculate`);
            }
          } else if (run?.status === 'failed') {
            showToast.error('Scope recalculation failed');
          }
        })
        .catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [latestRun?.status]);

  const fetchRules = async (): Promise<void> => {
    try {
      setLoading(true);
      const [flat, contractTypes, typeRefsRaw, categoryRefsRaw, divisions, settings, run, settingsHistory, supplierTiers] = await Promise.all([
        importService.getScopeRules(),
        contractService.getContractTypes().catch(() => [] as string[]),
        importService.getReferenceValues('contract_type').catch(() => [] as string[]),
        importService.getReferenceValues('category').catch(() => [] as string[]),
        divisionService.getActive().catch(() => [] as { id: number; name: string }[]),
        importService.getScopeSettings().catch(() => ({ global_scope_calculation: true })),
        importService.getLatestRecalculation().catch(() => null),
        importService.getScopeRulesHistory({ limit: SCOPE_RULES_HISTORY_PAGE_SIZE, offset: 0 }).catch(() => emptyHistory()),
        supplierTierAdminService.getAll().catch(() => []),
      ]);

      const typeRefs = Array.isArray(typeRefsRaw) ? typeRefsRaw : [];
      const categoryRefs = Array.isArray(categoryRefsRaw) ? categoryRefsRaw : [];

      const grouped = flatRulesToGrouped({
        rules: flat.rules.map((rule) => ({
          id: rule.id,
          field: rule.field,
          operator: rule.operator,
          value: rule.value,
          scope: rule.scope,
          created_at: rule.created_at ?? null,
          updated_at: rule.updated_at ?? null,
        })),
        total: flat.total,
      });

      const mergedTypes = mergeListRules(grouped.contract_type, [...contractTypes, ...typeRefs]);
      const mergedCategories = mergeListRules(grouped.category, categoryRefs);
      const mergedDivisions = mergeListRules(grouped.division, divisions.map((division) => division.name));

      setRules(grouped);
      setTierRules(mergeTierRules(grouped.tier, supplierTiers));
      setContractTypeRules(mergedTypes);
      setCategoryRules(mergedCategories);
      setDivisionRules(mergedDivisions);
      setAmountThreshold(grouped.amount?.min_threshold ?? 0);
      setDurationThreshold(grouped.duration?.min_threshold ?? 60);
      setGlobalScopeCalculation(settings.global_scope_calculation);
      setLatestRun(run);
      setHistory(settingsHistory.records);
      setHistoryTotal(settingsHistory.total);
      setHistoryPage(1);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching scope rules:', error);
      showToast.error('Failed to load scope rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryPage = async (page: number): Promise<void> => {
    const response = await importService.getScopeRulesHistory({
      limit: SCOPE_RULES_HISTORY_PAGE_SIZE,
      offset: (page - 1) * SCOPE_RULES_HISTORY_PAGE_SIZE,
    }).catch(() => emptyHistory());
    setHistory(response.records);
    setHistoryTotal(response.total);
    setHistoryPage(page);
  };

  const toggleListRule = (
    setter: React.Dispatch<React.SetStateAction<ListRule[]>>,
    key: string,
  ): void => {
    setter((prev) =>
      prev.map((item) =>
        (item.id !== undefined ? String(item.id) : item.value) === key
          ? { ...item, include_in_scope: !item.include_in_scope }
          : item
      )
    );
    setHasChanges(true);
  };

  const handleAmountChange = (value: string): void => {
    setAmountThreshold(Number(value) || 0);
    setHasChanges(true);
  };

  const handleDurationChange = (value: string): void => {
    setDurationThreshold(Number(value) || 0);
    setHasChanges(true);
  };

  const thresholdPayload = (threshold: ThresholdRule | null, value: number): ScopeRuleUpdatePayload[] => {
    if (!threshold?.id) {
      return [];
    }
    return [{ id: threshold.id, minThreshold: value }];
  };

  const handleSave = async (): Promise<void> => {
    try {
      setSaving(true);

      const rulesToUpdate: ScopeRuleUpdatePayload[] = [
        ...listRulesToPayload('tier', tierRules),
        ...listRulesToPayload('contract_type', contractTypeRules),
        ...listRulesToPayload('category', categoryRules),
        ...listRulesToPayload('division', divisionRules),
        ...thresholdPayload(rules?.amount ?? null, amountThreshold),
        ...thresholdPayload(rules?.duration ?? null, durationThreshold),
      ];

      await importService.updateScopeRules(rulesToUpdate);

      showToast.success('Scope rules saved. Recalculation started.');
      setHasChanges(false);
      await fetchRules();
    } catch (error) {
      console.error('Error saving scope rules:', error);
      showToast.error('Failed to save scope rules');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleGlobalScope = async (): Promise<void> => {
    try {
      setToggling(true);
      const next = !globalScopeCalculation;
      const settings = await importService.updateScopeSettings(next);
      setGlobalScopeCalculation(settings.global_scope_calculation);
      showToast.success(`Global Scope Calculation is ${settings.global_scope_calculation ? 'on' : 'off'}`);
      await fetchHistoryPage(1);
    } catch (error) {
      console.error('Error updating scope settings:', error);
      showToast.error('Failed to update Global Scope Calculation');
    } finally {
      setToggling(false);
    }
  };

  const handleRecalculate = async (): Promise<void> => {
    try {
      setRecalculating(true);
      const result = await importService.recalculateScopes();
      setShowRecalcConfirm(false);
      setLatestRun(result);
      showToast.success('Scope recalculation started');
    } catch (error) {
      console.error('Error recalculating scopes:', error);
      showToast.error('Failed to recalculate scopes');
    } finally {
      setRecalculating(false);
    }
  };

  const renderListSection = (
    title: string,
    description: string,
    items: ListRule[],
    onToggle: (key: string) => void,
  ): React.ReactNode => (
    <div className={styles.importSettingsSection}>
      <h3 className={styles.importSettingsSectionTitle}>{title}</h3>
      <p className={styles.importSettingsSectionDescription}>{description}</p>
      {items.length === 0 ? (
        <p className={styles.scopeRulesEmptyHelp}>{EMPTY_LOOKUP_HELP}</p>
      ) : (
        <div className={styles.scopeRulesTierGrid}>
          {items.map((item) => {
            const key = item.id !== undefined ? String(item.id) : item.value;
            return (
              <button
                type="button"
                key={key}
                className={clsx(styles.scopeRulesTierCard, {
                  [styles.scopeRulesTierCardIncluded]: item.include_in_scope,
                  [styles.scopeRulesTierCardExcluded]: !item.include_in_scope
                })}
                onClick={() => onToggle(key)}
              >
                <div className={styles.scopeRulesTierIcon}>
                  {item.include_in_scope ? (
                    <CheckCircle size={24} className={styles.scopeRulesIconSuccess} />
                  ) : (
                    <XCircle size={24} className={styles.scopeRulesIconError} />
                  )}
                </div>
                <div className={styles.scopeRulesTierContent}>
                  <span className={styles.scopeRulesTierName}>{item.value}</span>
                  <span className={clsx(styles.scopeRulesTierStatus, item.include_in_scope && styles.scopeRulesTierStatusIncluded)}>
                    {item.include_in_scope ? getScopeLabel('Include') : getScopeLabel('Exclude')}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={styles.importSettingsLoading}>
        <RefreshCw className="spin" size={24} />
        <span>Loading scope rules...</span>
      </div>
    );
  }

  return (
    <div className={styles.importSettings} data-test-id="scope-rules-settings">
      <SettingsSectionHeader
        sticky
        title="Scope Rules"
        description="Configure rules for automatic contract scope calculation."
        actions={
          <>
          <Button
            variant="secondary"
            onClick={() => setShowRecalcConfirm(true)}
            disabled={saving || recalculating || hasChanges || !globalScopeCalculation || latestRun?.status === 'running'}
            loading={recalculating}
            loadingText="Recalculating..."
            dataTestId="recalculate-all-button"
          >
            <RefreshCw size={16} />
            Recalculate All
          </Button>
          <Button
            variant="primary"
            onClick={() => { void handleSave(); }}
            disabled={saving || !hasChanges}
            loading={saving}
            loadingText="Saving..."
          >
            <Save size={16} />
            Save Changes
          </Button>
          </>
        }
      />

      <div className={styles.scopeToggleCard} data-test-id="global-scope-toggle">
        <div>
          <h3 className={styles.scopeToggleTitle}>Global Scope Calculation</h3>
          <p className={styles.scopeToggleHelp}>
            {globalScopeCalculation
              ? 'ON: create, update, Recalculate All, nightly jobs, and cascade triggers use scope rules. Import always calculates.'
              : 'OFF: only contract import calculates scope. Create/update, Recalculate All, nightly jobs, and cascade triggers skip the engine.'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={globalScopeCalculation}
          disabled={toggling}
          className={clsx(styles.scopeToggleSwitch, globalScopeCalculation && styles.scopeToggleSwitchOn)}
          onClick={() => { void handleToggleGlobalScope(); }}
          data-test-id="global-scope-toggle-button"
        >
          <span className={styles.scopeToggleKnob} />
          <span className={styles.scopeToggleLabel}>{globalScopeCalculation ? 'ON' : 'OFF'}</span>
        </button>
      </div>

      {latestRun && (
        <div className={styles.recalcStatusPanel} data-test-id="recalculation-status">
          <div className={styles.recalcStatusHeader}>
            <h3>Recalculation status</h3>
            <span className={clsx(styles.recalcStatusBadge, latestRun.status === 'completed' && styles.recalcStatusCompleted, latestRun.status === 'failed' && styles.recalcStatusFailed, latestRun.status === 'running' && styles.recalcStatusRunning)}>
              {latestRun.status}
            </span>
          </div>
          <p className={styles.recalcStatusMeta}>
            Trigger: {latestRun.trigger}
            {latestRun.completed_at ? ` · Completed ${new Date(latestRun.completed_at).toLocaleString()}` : ''}
          </p>
          <div className={styles.recalcStatusGrid}>
            <span>Evaluated {latestRun.total}</span>
            <span>Include {latestRun.include_count}</span>
            <span>Exclude {latestRun.exclude_count}</span>
            <span>Flagged {latestRun.flagged}</span>
            <span>Errors {latestRun.error_count}</span>
          </div>
        </div>
      )}

      <div className={styles.importSettingsInfoBanner}>
        <Info size={16} />
        <span>
          These rules determine whether a contract is included in or excluded from assessments when Assessment Scope is set to Calculate.
          They are applied when creating or updating a contract, during import, and when you run Recalculate All.
        </span>
      </div>

      {renderListSection(
        'Tier Rules',
        'Select which supplier tiers should be included in assessment scope',
        tierRules,
        (key) => toggleListRule(setTierRules, key),
      )}

      <div className={styles.importSettingsSection}>
        <h3 className={styles.importSettingsSectionTitle}>Threshold Rules</h3>
        <p className={styles.importSettingsSectionDescription}>Set minimum values for contracts to be included in scope</p>

        <div className={styles.scopeRulesThresholdGrid}>
          <div className={styles.scopeRulesThresholdCard}>
            <div className={styles.scopeRulesThresholdHeader}>
              <span className={styles.scopeRulesThresholdLabel}>Minimum Duration</span>
              <AlertTriangle size={16} className={styles.scopeRulesThresholdWarning} />
            </div>
            <div className={styles.scopeRulesThresholdInputGroup}>
              <input
                type="number"
                className={styles.scopeRulesThresholdInput}
                value={durationThreshold}
                onChange={(e) => handleDurationChange(e.target.value)}
                min={0}
                step={1}
              />
              <span className={styles.scopeRulesThresholdUnit}>days</span>
            </div>
            <p className={styles.scopeRulesThresholdHelp}>Contracts shorter than this will be excluded from scope</p>
          </div>

          <div className={styles.scopeRulesThresholdCard}>
            <div className={styles.scopeRulesThresholdHeader}>
              <span className={styles.scopeRulesThresholdLabel}>Minimum Amount</span>
              <AlertTriangle size={16} className={styles.scopeRulesThresholdWarning} />
            </div>
            <div className={styles.scopeRulesThresholdInputGroup}>
              <span className={styles.scopeRulesThresholdCurrency}>$</span>
              <input
                type="number"
                className={styles.scopeRulesThresholdInput}
                value={amountThreshold}
                onChange={(e) => handleAmountChange(e.target.value)}
                min={0}
                step={1000}
              />
            </div>
            <p className={styles.scopeRulesThresholdHelp}>Contracts below this amount will be excluded from scope</p>
          </div>
        </div>
      </div>

      {renderListSection(
        'Contract Types',
        'Select which contract types should be included in assessment scope',
        contractTypeRules,
        (key) => toggleListRule(setContractTypeRules, key),
      )}

      {renderListSection(
        'Categories',
        'Select which categories should be included in assessment scope',
        categoryRules,
        (key) => toggleListRule(setCategoryRules, key),
      )}

      {renderListSection(
        'Divisions',
        'Select which divisions should be included in assessment scope',
        divisionRules,
        (key) => toggleListRule(setDivisionRules, key),
      )}

      {hasChanges && (
        <div className={styles.importSettingsUnsavedBanner}>
          <AlertTriangle size={16} />
          <span>You have unsaved changes. Click &quot;Save Changes&quot; to apply them.</span>
        </div>
      )}

      <div className={styles.settingsHistory} data-test-id="scope-settings-history">
        <div className={styles.settingsHistoryHeader}>
          <History size={18} />
          <h3>Settings change history</h3>
        </div>
        <p className={styles.settingsHistoryHelp}>
          Who changed which scope calculation setting, with previous and new values.
        </p>
        {history.length === 0 ? (
          <p className={styles.settingsHistoryEmpty}>No setting changes recorded yet.</p>
        ) : (
          <>
            <ul className={styles.settingsHistoryList}>
              {history.map((entry) => (
                <li key={entry.id} className={styles.settingsHistoryItem}>
                  <div className={styles.settingsHistoryMeta}>
                    <span className={styles.settingsHistoryUser}>{entry.user_name || 'System'}</span>
                    <span className={styles.settingsHistoryDate}>{formatMediumDateTime(entry.created_at)}</span>
                  </div>
                  {entry.changes?.length ? (
                    <div className={styles.settingsHistoryChanges}>
                      {entry.changes.map((change) => (
                        <div key={`${entry.id}-${change.field_name}`} className={styles.settingsHistoryChange}>
                          <span className={styles.settingsHistoryField}>{change.field_name}</span>
                          <span className={styles.settingsHistoryOld}>{change.old_value ?? '—'}</span>
                          <span aria-hidden="true">→</span>
                          <span className={styles.settingsHistoryNew}>{change.new_value ?? '—'}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {entry.notes ? <p className={styles.settingsHistoryNotes}>{entry.notes}</p> : null}
                </li>
              ))}
            </ul>
            <div className={styles.settingsHistoryPagination} data-test-id="scope-settings-history-pagination">
              <Pagination
                currentPage={historyPage}
                totalPages={Math.max(1, Math.ceil(historyTotal / SCOPE_RULES_HISTORY_PAGE_SIZE))}
                totalItems={historyTotal}
                pageSize={SCOPE_RULES_HISTORY_PAGE_SIZE}
                onPageChange={(page) => { void fetchHistoryPage(page); }}
                itemName="events"
              />
            </div>
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={showRecalcConfirm}
        onClose={() => setShowRecalcConfirm(false)}
        onConfirm={() => { void handleRecalculate(); }}
        title="Recalculate all contracts?"
        message="Recalculate All re-evaluates every Calculate-mode contract against current rules. Manual Include/Exclude overrides are not overwritten."
        confirmLabel="Recalculate All"
        confirmVariant="danger"
        isProcessing={recalculating}
        dataTestId="recalculate-confirmation-modal"
      />
    </div>
  );
};

export default ScopeRulesSettings;
